import { useCallback } from 'react';
import axiosClient from 'api/axiosClient';
import useChatMessages from '../../store/conversations/useChatMessages';
import useFocusChats from '../../store/conversations/useFocusChats';

export default function useChatMessagesHooks() {
  const { setState, messages, hasMoreMessages, updateModerationByFocusMessageId } = useChatMessages();
  const { removeChatById } = useFocusChats();

  const fetchMore = async (chatId: string | undefined) => {
    if (!hasMoreMessages) return;

    const firstMessage = messages[0];
    if (!firstMessage) return;

    await fetchChatMessages({
      chatId,
      limit: 10,
      createdAt: firstMessage.createdAt,
      useExisting: true,
    });
  };

  const fetchChatMessages = async (payload: any) => {
    setState({ error: false, isLoading: true });

    try {
      const response = await axiosClient.post('conversations/get-messages', payload);

      if (response?.status === 200) {
        const { data } = response.data;

        const isSearch = Boolean(payload.search && payload.search.trim() !== '');
        const useExisting = Boolean(payload.useExisting);

        const incomingMessages = (data.messages || []).reverse();

        const existingMessages =
            useExisting && messages?.[0]?.chatId === payload.chatId
                ? messages || []
                : [];

        const mergedMessages = isSearch
            ? incomingMessages
            : useExisting
                ? [...incomingMessages, ...existingMessages]
                : incomingMessages;

        const dedupedMessages = Array.from(
            new Map(mergedMessages.map((m: any) => [m._id, m])).values()
        );

        setState({
          messages: dedupedMessages,
          hasMoreMessages: data.isNextPage,
          isLoading: false,
          error: false,
        });
      } else {
        setState({
          messages: [],
          error: true,
          isLoading: false,
        });
      }
    } catch (error) {
      setState({
        error: true,
        isLoading: false,
      });
    }
  };

  const fetchFocusChatMessages = async (payload: any) => {
    setState({ error: false, isLoading: true });

    try {
      const response = await axiosClient.post('conversations/focus-messages', payload).then(res => res.data);
      if (!response?.error) {
        const { data } = response;
        const incomingMessages = data.reverse();

        setState({
          messages: incomingMessages,
          isLoading: false,
          error: false,
        });
      }
    } catch (error) {
      setState({ error: true, isLoading: false });
    }
  };

  const fetchChatMessageById = async (chatId: string, messageId: string) => {
    try {
      const response = await axiosClient.get(`/conversations/${chatId}/messages/${messageId}`);
      if (!response.data?.error) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching chat message:', error);
      return null;
    }
  };

  const fetchChatParticipants = useCallback(
    async (chatId: string) => {
      try {
        const response = await axiosClient.get(`conversations/${chatId}/chat-participants`);

        const data = response.data;
        if (data?.error) return;

        setState({
          chatParticipants: data?.data ?? [],
        });
      } catch (error) {
        setState({ error: true });
      }
    },
    [setState]
  );

  const markMessageFlaggedByAdmin = async (chatId: string, messageId: string, isMessageSafe: boolean) => {
    setState({ isFlaggingMessage: true, flagError: null });

    try {
      const response = await axiosClient.post(
        `/conversations/${chatId}/focus-messages/${messageId}/admin-moderation-check`,
        {
          isMessageSafe: isMessageSafe,
        }
      );

      if (!response.data?.error) {
        updateModerationByFocusMessageId(messageId, isMessageSafe);
        removeChatById(messageId);

        setState({
          isFlaggingMessage: false,
        });

        return response.data.data;
      } else {
        setState({
          isFlaggingMessage: false,
          flagError: 'Failed to update message safety flag. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error updating message safety flag:', error);
      setState({
        isFlaggingMessage: false,
        flagError: 'Failed to update message safety flag. Please try again.',
      });
    }
  };

  return {
    fetchChatMessages,
    fetchFocusChatMessages,
    fetchMore,
    fetchChatParticipants,
    fetchChatMessageById,
    markMessageFlaggedByAdmin,
  };
}
